/**
 * Watch-zone promotion gating.
 *
 * Watch zones are a high-value feature but were buried in the bottom-
 * sheet footer. We surface them contextually in three places:
 *   1. Onboarding nudge on first visit
 *   2. "Watch this area" pill inside FireDetailPanel
 *   3. Empty-state CTA when the user filters to a quiet area
 *
 * This module centralises the gating so the prompts:
 *   - never show to a user who's already signed in AND has ≥ 1 zone
 *     (they know about the feature; nagging is hostile);
 *   - each remember a per-pattern dismissal so a "not interested" tap
 *     stops that pattern for good without affecting the others;
 *   - share one storage prefix so the keys are easy to audit/clear.
 */

export type PromotionId =
  | 'watchzone-onboard'        // First-visit nudge (#1)
  | 'watchzone-detail-pill'    // FireDetailPanel pill (#2)
  | 'watchzone-empty-state';   // Empty-state CTA (#3)

const STORAGE_PREFIX = 'firewatch:promotion-dismissed:';

export function isPromotionDismissed(id: PromotionId): boolean {
  try {
    return localStorage.getItem(STORAGE_PREFIX + id) === 'true';
  } catch {
    return false;
  }
}

export function dismissPromotion(id: PromotionId): void {
  try {
    localStorage.setItem(STORAGE_PREFIX + id, 'true');
  } catch {
    /* localStorage may be unavailable in private mode */
  }
}

/**
 * Should the given watch-zone promotion be shown right now?
 * The suppression rule has two limbs:
 *   - users who already have zones don't need promotion;
 *   - users who've dismissed THIS specific prompt shouldn't see it again.
 */
export function shouldShowWatchZonePromotion(args: {
  isAuthenticated: boolean;
  zoneCount: number;
  promotionId: PromotionId;
}): boolean {
  if (args.isAuthenticated && args.zoneCount > 0) return false;
  if (isPromotionDismissed(args.promotionId)) return false;
  return true;
}
